import React, { useEffect, useRef } from "react";
import { FieldError, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import Input from "../common/Input";
import Dropdown from "../common/Dropdown";
import Button from "../common/Button";
import { playlistSchema, PlaylistFormData } from "../../schemas/playlistSchema";
import Modal from "../common/Modal";
import { usePlaylistTagOptions } from "../../hooks/usePlaylistTagOptions";

interface PlaylistFormProps {
  visible: boolean;
  initialData?: Partial<PlaylistFormData>;
  onSubmit: (data: PlaylistFormData) => void;
  onCancel: () => void;
  isLoading?: boolean;
  mode?: "create" | "clone";
}

const typeOptions = [
  { value: "main", label: "Main" },
  { value: "sub", label: "Sub" },
];

const PlaylistForm: React.FC<PlaylistFormProps> = ({
  visible,
  initialData,
  onSubmit,
  onCancel,
  isLoading = false,
  mode = "create",
}) => {
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
    trigger,
  } = useForm<PlaylistFormData>({
    resolver: zodResolver(playlistSchema),
    mode: "onBlur",
    reValidateMode: "onBlur",
    defaultValues: {
      name: "",
      type: undefined,
      tagIds: [],
      ...initialData,
    },
  });

  const { playlistTagOptions, isPlaylistTagOptionsLoading } =
    usePlaylistTagOptions();

  // Track if modal was just opened to prevent resetting during submission
  const prevVisibleRef = useRef(false);

  // Reset form only when modal opens (not on every render or during submission)
  useEffect(() => {
    // Only reset when modal transitions from closed to open
    if (visible && !prevVisibleRef.current) {
      if (initialData) {
        reset({
          name: initialData.name || "",
          type: initialData.type,
          tagIds: initialData.tagIds || [],
        });
      } else {
        reset({
          name: "",
          type: undefined,
          tagIds: [],
        });
      }
    }
    prevVisibleRef.current = visible;
  }, [visible, initialData, reset]);

  const getTitle = () => {
    switch (mode) {
      case "clone":
        return "Clone Playlist";
      default:
        return "Create Playlist";
    }
  };

  const getSubmitButtonText = () => {
    switch (mode) {
      case "clone":
        return "Clone Playlist";
      default:
        return "Create";
    }
  };

  const handleFormSubmit = (data: PlaylistFormData) => {
    onSubmit(data);
  };

  return (
    <Modal isOpen={visible} onClose={onCancel} title={getTitle()}>
      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
        <Input
          label="Playlist Name"
          placeholder="Enter playlist name"
          name="name"
          value={watch("name") || ""}
          onChange={(e) => setValue("name", e.target.value, { shouldValidate: false })}
          onBlur={() => trigger("name")}
          error={errors.name}
        />

        {mode !== "clone" && (
          <>
            <Dropdown
              label="Type"
              options={typeOptions}
              value={watch("type")}
              name="type"
              onChange={(val) => setValue("type", val as "main" | "sub", { shouldValidate: false })}
              onBlur={() => trigger("type")}
              error={errors.type}
            />
            <Dropdown
              label="Tags"
              multiple
              options={playlistTagOptions || []}
              value={watch("tagIds")?.map((id) => String(id)) || []}
              name="tagIds"
              onChange={(vals) => setValue("tagIds", (vals as string[]) || [], { shouldValidate: false })}
              onBlur={() => trigger("tagIds")}
              error={(errors.tagIds as FieldError) || undefined}
              disabled={isPlaylistTagOptionsLoading}
            />
          </>
        )}

        <div className="flex justify-end space-x-4">
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            isLoading={isLoading}
            className="w-full sm:w-auto"
          >
            {getSubmitButtonText()}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default PlaylistForm;
